import { useState } from 'react'
import { BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, Legend, ResponsiveContainer } from 'recharts'
import { TrendingUp, Loader2, BarChart3, Layers, Table2 } from 'lucide-react'

// Base colors for main operators and known sub-groups.
const OPERATOR_COLORS = {
  ORANGE: '#FF7900',
  ORANGE_OPASKA_SOS: '#FF7900',
  ORANGE_LOKALIZATOR: '#FFB366',
  TMOBILE: '#E20074',
  PLUS: '#00A651',
  P4: '#9333EA',
}

// Fallback display names. Dynamic names from product_groups API take priority
// via the groupNames prop (see getSeriesName).
const OPERATOR_NAMES = {
  ORANGE: 'Orange',
  TMOBILE: 'T-Mobile',
  PLUS: 'Plus',
  P4: 'Play',
}

function getSeriesName(code, groupNames) {
  // Prefer dynamic name from product_groups DB, fall back to hardcoded map
  if (groupNames?.[code]) return groupNames[code]
  return OPERATOR_NAMES[code] || code.replace(/_/g, ' ')
}

function getSeriesColor(code, idx) {
  if (OPERATOR_COLORS[code]) return OPERATOR_COLORS[code]
  // Fallback palette for unknown sub-group keys
  const palette = ['#6366F1', '#F59E0B', '#10B981', '#EF4444', '#8B5CF6']
  return palette[idx % palette.length]
}

const DAY_OPTIONS = [14, 30, 60, 90]

const VIEW_MODES = [
  { key: 'stacked', label: 'Laczny', icon: Layers },
  { key: 'grouped', label: 'Osobno', icon: BarChart3 },
  { key: 'table', label: 'Tabela', icon: Table2 },
]

const DailySalesChart = ({ data, loading, currentDays, onChangeDays, operators, groupNames = {} }) => {
  const [viewMode, setViewMode] = useState('stacked')

  // Build series: for each base operator, check if the response contains
  // per-group sub-keys (e.g. ORANGE_OPASKA, ORANGE_LOCATOR). If so, show
  // the sub-groups instead of the aggregate. This is driven by backend
  // product_groups config — no hardcoded operator names here.
  const allKeys = Object.keys(data || {})
  const baseOperatorCodes = operators
    ? operators.map(op => op.code)
    : allKeys.filter(k => !k.includes('_') || !allKeys.includes(k.split('_')[0]))

  const operatorCodes = baseOperatorCodes.flatMap(code => {
    const subKeys = allKeys.filter(k => k.startsWith(code + '_'))
    return subKeys.length > 0 ? subKeys : [code]
  })

  // Build chart data: merge all operators into one array keyed by date
  const { chartData, rawDates } = (() => {
    if (!data) return { chartData: [], rawDates: [] }

    const dateSet = new Set()
    operatorCodes.forEach(code => {
      ;(data[code] || []).forEach(d => dateSet.add(d.date))
    })

    const dates = Array.from(dateSet).sort()

    const lookups = {}
    operatorCodes.forEach(code => {
      lookups[code] = {}
      ;(data[code] || []).forEach(d => {
        lookups[code][d.date] = d.count
      })
    })

    const rows = dates.map(date => {
      const entry = { date: formatDate(date), rawDate: date }
      let total = 0
      operatorCodes.forEach(code => {
        const val = lookups[code][date] || 0
        entry[code] = val
        total += val
      })
      entry._total = total
      return entry
    })

    return { chartData: rows, rawDates: dates }
  })()

  // Totals per operator for table footer
  const totals = {}
  let grandTotal = 0
  operatorCodes.forEach(code => {
    const sum = chartData.reduce((acc, row) => acc + (row[code] || 0), 0)
    totals[code] = sum
    grandTotal += sum
  })

  const renderChart = () => {
    const isStacked = viewMode === 'stacked'
    return (
      <ResponsiveContainer width="100%" height={350}>
        <BarChart data={chartData} margin={{ top: 5, right: 20, left: 0, bottom: 5 }}>
          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" />
          <XAxis dataKey="date" tick={{ fontSize: 12 }} tickLine={false} />
          <YAxis tick={{ fontSize: 12 }} tickLine={false} axisLine={false} />
          <Tooltip
            content={({ active, payload, label }) => {
              if (!active || !payload?.length) return null
              const items = isStacked ? [...payload].reverse() : payload
              const total = payload.reduce((sum, p) => sum + (p.value || 0), 0)
              return (
                <div className="bg-white rounded-lg border border-gray-200 shadow-lg px-3 py-2 text-sm">
                  <div className="font-semibold text-gray-800 mb-1">{label}</div>
                  {items.map(p => (
                    <div key={p.dataKey} className="flex justify-between gap-4" style={{ color: p.fill }}>
                      <span>{getSeriesName(p.dataKey, groupNames)}</span>
                      <span className="font-medium tabular-nums">{p.value}</span>
                    </div>
                  ))}
                  {payload.length > 1 && (
                    <div className="flex justify-between gap-4 border-t border-gray-200 mt-1 pt-1 font-semibold text-gray-800">
                      <span>Suma</span>
                      <span className="tabular-nums">{total}</span>
                    </div>
                  )}
                </div>
              )
            }}
          />
          <Legend formatter={(value) => getSeriesName(value, groupNames)} />
          {operatorCodes.map((code, idx) => (
            <Bar
              key={code}
              dataKey={code}
              stackId={isStacked ? 'stack' : undefined}
              fill={getSeriesColor(code, idx)}
              radius={isStacked ? undefined : [2, 2, 0, 0]}
              maxBarSize={isStacked ? undefined : 40}
            />
          ))}
        </BarChart>
      </ResponsiveContainer>
    )
  }

  const renderTable = () => (
    <div className="overflow-x-auto">
      <table className="w-full text-sm">
        <thead>
          <tr className="border-b border-gray-200">
            <th className="text-left py-2 px-3 font-semibold text-gray-600">Data</th>
            {operatorCodes.map((code, idx) => (
              <th key={code} className="text-right py-2 px-3 font-semibold" style={{ color: getSeriesColor(code, idx) }}>
                {getSeriesName(code, groupNames)}
              </th>
            ))}
            <th className="text-right py-2 px-3 font-semibold text-gray-800">Suma</th>
          </tr>
        </thead>
        <tbody>
          {[...chartData].reverse().map((row, i) => (
            <tr key={row.rawDate} className={i % 2 === 0 ? 'bg-gray-50' : ''}>
              <td className="py-1.5 px-3 text-gray-700 font-medium">{row.date}</td>
              {operatorCodes.map(code => (
                <td key={code} className="text-right py-1.5 px-3 tabular-nums">
                  {row[code] || 0}
                </td>
              ))}
              <td className="text-right py-1.5 px-3 font-semibold tabular-nums">{row._total}</td>
            </tr>
          ))}
        </tbody>
        <tfoot>
          <tr className="border-t-2 border-gray-300 font-bold">
            <td className="py-2 px-3 text-gray-800">Suma</td>
            {operatorCodes.map((code, idx) => (
              <td key={code} className="text-right py-2 px-3 tabular-nums" style={{ color: getSeriesColor(code, idx) }}>
                {totals[code]}
              </td>
            ))}
            <td className="text-right py-2 px-3 tabular-nums text-gray-800">{grandTotal}</td>
          </tr>
        </tfoot>
      </table>
    </div>
  )

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 p-6">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
        <h3 className="text-lg font-semibold text-gray-800 flex items-center">
          <TrendingUp className="h-5 w-5 mr-2 text-brand-blue" />
          Sprzedaz dzienna per operator
        </h3>
        <div className="flex items-center gap-3">
          {/* View mode toggle */}
          <div className="flex items-center bg-gray-100 rounded-lg p-0.5">
            {VIEW_MODES.map(({ key, label, icon: Icon }) => (
              <button
                key={key}
                onClick={() => setViewMode(key)}
                className={`flex items-center gap-1 px-2.5 py-1.5 text-xs rounded-md font-medium transition-colors ${
                  viewMode === key
                    ? 'bg-white text-gray-800 shadow-sm'
                    : 'text-gray-500 hover:text-gray-700'
                }`}
              >
                <Icon className="h-3.5 w-3.5" />
                {label}
              </button>
            ))}
          </div>
          {/* Day selector */}
          <div className="flex items-center gap-1">
            {DAY_OPTIONS.map(days => (
              <button
                key={days}
                onClick={() => onChangeDays(days)}
                disabled={loading}
                className={`px-3 py-1.5 text-sm rounded-lg font-medium transition-colors ${
                  currentDays === days
                    ? 'bg-blue-500 text-white'
                    : 'bg-gray-100 text-gray-600 hover:bg-gray-200'
                } ${loading ? 'opacity-50 cursor-not-allowed' : ''}`}
              >
                {days}d
              </button>
            ))}
          </div>
        </div>
      </div>

      {loading && !data ? (
        <div className="flex items-center justify-center h-64 text-gray-400">
          <Loader2 className="h-6 w-6 animate-spin mr-2" />
          Ladowanie danych...
        </div>
      ) : chartData.length === 0 ? (
        <div className="flex items-center justify-center h-64 text-gray-400">
          Brak danych do wyswietlenia
        </div>
      ) : (
        <div className="relative">
          {loading && (
            <div className="absolute top-2 right-2 z-10">
              <Loader2 className="h-4 w-4 animate-spin text-blue-500" />
            </div>
          )}
          {viewMode === 'table' ? renderTable() : renderChart()}
        </div>
      )}
    </div>
  )
}

function formatDate(dateStr) {
  const parts = dateStr.split('-')
  return `${parts[2]}.${parts[1]}`
}

export default DailySalesChart
